import {ResultColumn} from "./reportApi";

/**
 * A report result, as something a chart can draw — and, more often, the reason it cannot.
 *
 * NAMED chartShape AND NOT reportChart, because ReportChart.tsx renders it and the two would differ
 * only in case. macOS is case-insensitive, so TypeScript sees one file and reports "differs from
 * already included file name only in casing" -- a build error here, and worse on a case-SENSITIVE
 * CI box where both exist and the wrong one may be resolved.
 *
 * A TABLE AND A CHART WANT DIFFERENT SHAPES. A grid renders any result: ten dimensions, no measure,
 * whatever. A chart needs a category axis and something numeric to plot against it, and a result
 * that does not have those cannot be drawn no matter which chart type somebody picks.
 *
 * SO THE ANSWER IS "WHY NOT", NOT A BLANK PANEL. Every chart tool has the failure where you switch
 * to bar and get an empty rectangle; the shape is wrong and nothing says so. Each type here reports
 * what it needs and what this result has, so the toggle can be greyed with a reason.
 *
 * WHY THE NUMBERS ARE PARSED HERE. The rows arrive as TEXT, deliberately: the server stores and
 * returns exactly what the reader saw, because a number re-parsed in a browser is a second answer
 * waiting to disagree about rounding. A chart needs actual numbers, so this is the one place that
 * conversion happens -- and a value that will not parse becomes null and a gap in the line rather
 * than a zero, because a zero is a claim and a gap is the truth.
 */

export type ChartKind = "bar" | "line" | "pie";

export type ChartData = {
    /** The category axis: one label per row. */
    categories: string[];
    /** One series per measure column. */
    series: {label: string; data: (number | null)[]}[];
};

export type ChartRefusal = {why: string};

const NUMERIC = /^-?\d+(\.\d+)?$/;

/**
 * A cell as a number, or null.
 *
 * EXPORTED, because chartSpec's rules need the same answer. Rows arrive as TEXT on purpose -- the
 * server stores and returns exactly what the reader saw -- so this is the ONE place that conversion
 * happens. A second rule about what counts as a number is a second answer waiting to disagree.
 */
export function toNumber(value: unknown): number | null {
    const text = String(value ?? "").trim();
    if (!text || !NUMERIC.test(text)) return null;
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : null;
}

function split(columns: ResultColumn[]) {
    return {
        dimensions: columns.filter(one => one.Role === "dimension"),
        measures: columns.filter(one => one.Role === "measure"),
    };
}

/** Why this result cannot be drawn as `kind`, or null when it can. */
export function chartRefusal(kind: ChartKind, columns: ResultColumn[],
                             rows: unknown[][]): ChartRefusal | null {
    if (!columns.length || !rows.length) return {why: "Run the report first"};

    const {dimensions, measures} = split(columns);
    if (!dimensions.length) return {why: "needs something to group by"};
    if (!measures.length) return {why: "needs something summarized"};

    // ONE CATEGORY AXIS. Two dimensions is a grouped or stacked chart -- a real thing, and a
    // different piece of work -- so it refuses by name rather than silently charting the first and
    // dropping the second, which would be a chart that disagrees with the table beside it.
    if (dimensions.length > 1) {
        return {why: `has ${dimensions.length} things to group by; a chart can show one`};
    }
    if (kind === "pie" && measures.length > 1) {
        return {why: "a pie shows one measure; this has " + measures.length};
    }
    // A PIE OF NEGATIVES IS NOT A PIE. Slices are shares of a whole and a negative share has no
    // area; every library draws something anyway, and what it draws is a lie.
    if (kind === "pie") {
        const index = columns.indexOf(measures[0]);
        if (rows.some(row => (toNumber(row[index]) ?? 0) < 0)) {
            return {why: "a pie cannot show negative values"};
        }
    }
    return null;
}

/** The result as categories and series, or null when it cannot be drawn. */
export function chartData(kind: ChartKind, columns: ResultColumn[],
                          rows: unknown[][]): ChartData | null {
    if (chartRefusal(kind, columns, rows)) return null;

    const {dimensions, measures} = split(columns);
    const categoryAt = columns.indexOf(dimensions[0]);

    return {
        categories: rows.map(row => String(row[categoryAt] ?? "")),
        series: measures.map(measure => {
            const at = columns.indexOf(measure);
            return {label: measure.Label, data: rows.map(row => toNumber(row[at]))};
        }),
    };
}

/** Which chart types this result can be drawn as, with a reason for the ones it cannot. */
export function chartOptions(columns: ResultColumn[], rows: unknown[][]) {
    return (["bar", "line", "pie"] as ChartKind[]).map(kind => ({
        kind,
        label: kind === "bar" ? "Bar" : kind === "line" ? "Line" : "Pie",
        refusal: chartRefusal(kind, columns, rows),
    }));
}
